import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { put } from '@nineeng/request';
import Typography from '@mui/material/Typography';
import { DetailsTab, RequirementChip, Badge, Spinner } from '@repo/components';
import { PERMISSIONS, LanguageCode } from '@repo/shared/domain';
import { EventEmitter, getVerifyPermission } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { getCompanyEvent, getEventForm } from 'src/api/company';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { getCompanyEventAccess } from 'src/helpers/companyEventAccess';

export const Route = createFileRoute('/company_/$groupId/events/$eventId/details')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>) => ({
		language: (search.language as string) || '',
	}),
});

function RouteComponent() {
	const { groupId, eventId } = Route.useParams();
	const { language: searchLanguage } = Route.useSearch();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const queryClient = useQueryClient();
	const updateQueryParams = useUpdateQueryParams();

	const { data: event, isLoading: eventLoading } = useQuery({
		queryKey: ['company-event', groupId, eventId],
		queryFn: () => getCompanyEvent(groupId, eventId),
		enabled: !userLoading,
	});

	const formId = event?.type?.form;
	const { data: form } = useQuery({
		queryKey: ['event-form', formId, searchLanguage],
		queryFn: () => getEventForm(formId!, searchLanguage ? `?language=${searchLanguage}` : ''),
		enabled: !!formId,
	});

	useBreadcrumbLevel(3, 'Details');

	if (userLoading || eventLoading) {
		return <Spinner className="text-[#1E1E1E]" />;
	}

	if (!authenticatedUser || !event) return null;

	const verifyPermission = getVerifyPermission(authenticatedUser.permissions);
	const { isReadOnlyEvent } = getCompanyEventAccess(event, groupId, authenticatedUser?.id);
	const users = event?.users?.filter((user: any) => user.status === 'active');

	const printHeader = (
		<div className="flex gap-2">
			<div className="w-[300px] flex items-center">
				<img style={{ objectFit: 'contain', maxHeight: 70, width: '100%' }} src="/api/public/logo" />
			</div>
			<div className="flex-grow flex flex-row flex-wrap p-2">
				<div className="w-[30%]">
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Event Name
					</Typography>
					<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
						{event?.name || '-'}
					</Typography>
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Type
					</Typography>
					<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
						{event?.type ? <RequirementChip object={event} /> : '-'}
					</Typography>
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Description
					</Typography>
					<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
						{event?.description || '-'}
					</Typography>
				</div>
				<div className="w-[30%]">
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Start Time
					</Typography>
					<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
						{event?.startTime ? new Date(event.startTime).toLocaleString('en-GB') : '-'}
					</Typography>
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Finish Time
					</Typography>
					<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
						{event?.finishTime ? new Date(event.finishTime).toLocaleString('en-GB') : '-'}
					</Typography>
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Zones
					</Typography>
					<div className="min-h-[29px] flex flex-wrap gap-1">
						{event?.zones?.length ? event.zones.map((r: any) => <Badge key={r.name}>{r.name}</Badge>) : '-'}
					</div>
				</div>
				<div className="w-[40%]">
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Owners
					</Typography>
					<div className="min-h-[29px] flex flex-wrap gap-1">
						{event?.admins?.length ? event.admins.map((r: any) => <Badge key={r.email}>{r.email}</Badge>) : '-'}
					</div>
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Companies
					</Typography>
					<div className="min-h-[29px] flex flex-wrap gap-1">
						{event?.groups?.length ? event.groups.map((r: any) => <Badge key={r.name}>{r.name}</Badge>) : '-'}
					</div>
				</div>
				<div className="w-full">
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Users
					</Typography>
					<div className="min-h-[29px] flex flex-wrap gap-1">
						{users?.length
							? users.map((r: any) => (
									<Badge key={r.email}>
										{r.firstName} {r.lastName}
									</Badge>
								))
							: '-'}
					</div>
				</div>
			</div>
		</div>
	);

	const handleDetailsUpdate = async (eventData: any) => {
		await put(`/api/company-portal/groups/${groupId}/events/${eventId}/details`, { ...eventData })
			.then(() => EventEmitter.emit('toast', { type: 'success', message: 'Changes saved' }))
			.catch(() => EventEmitter.emit('toast', { type: 'error', message: 'Error' }));
		queryClient.invalidateQueries({ queryKey: ['company-event', groupId, eventId] });
	};

	const handleLanguageChange = (language: string) => {
		updateQueryParams({ language });
	};

	const availableLanguages = form?.availableLanguages ?? [];
	const defaultLanguage = form?.language ?? 'en';
	const languages = [...new Set([...availableLanguages, defaultLanguage])] as LanguageCode[];
	const language = languages.find((l) => l === searchLanguage) || 'en';

	return (
		<DetailsTab
			data={{
				...event.eventData,
				eventId,
				endpointPrefix: `/api/company-portal/groups/${groupId}`,
			}}
			printHeader={printHeader}
			form={form}
			onLanguageChange={handleLanguageChange}
			language={language}
			availableLanguages={languages}
			onSave={handleDetailsUpdate}
			readOnly={
				isReadOnlyEvent ||
				(!verifyPermission(PERMISSIONS.companyEventsEdit) &&
				!verifyPermission(PERMISSIONS.companyMaincontractorEventsEditDetails))
			}
		/>
	);
}
